import React, { memo, useCallback, useState } from 'react';
import Container from '../Container';
import {
  IconFacebook24,
  IconInstagram24,
  IconTelegram24,
  IconVk24,
  IconYoutube24,
} from '@app/icons';
import { Link } from 'react-router-dom';
import { TenderCategory, UserType } from '@app/models';
import { ModalTenderCreate } from '@app/modals';
import { useUserContext } from '@app/providers';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { cn } from 'lib/utils';
import { useNotification } from '@app/providers';

interface Props {
  className?: string;
}

function Footer(props: Props) {
  const { user, showSignInModal, company } = useUserContext();
  const [tenderCategory, setTenderCategory] = useState<TenderCategory>(
    TenderCategory.CONTRACTOR
  );
  const [modalTenderCreateVisible, setModalTenderCreateVisible] =
    useState<boolean>(false);
  const { showNotification } = useNotification();

  const closeModalTenderCreate = useCallback(() => {
    setModalTenderCreateVisible(false);
  }, []);

  const handleTenderCreateSuccess = useCallback(() => {
    showNotification({ message: 'Заявка создалась', variant: 'success' });
  }, [showNotification]);

  const onClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onClickRequest = useCallback(
    (category: TenderCategory) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!user.authenticated) {
        showSignInModal();
        return;
      }

      setTenderCategory(category);
      setModalTenderCreateVisible(true);
    },
    [showSignInModal, user]
  );

  return (
    <footer className={cn('bg-white py-8 mt-8 text-center', props.className)}>
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left pb-6">
          <div className="flex flex-col space-y-2">
            <h3 className="text-black font-medium text-base leading-6 tracking-wide m-0">
              Заявка
            </h3>
            {(!user || (!!user && user.type !== UserType.INDIVIDUAL)) && (
              <Button
                variant="link"
                asChild
                className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
              >
                <a href="#" onClick={onClickRequest(TenderCategory.CONTRACTOR)}>
                  Заявка на СМР
                </a>
              </Button>
            )}
            {(!user || (!!user && user.type !== UserType.INDIVIDUAL)) && (
              <Button
                variant="link"
                asChild
                className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
              >
                <a href="#" onClick={onClickRequest(TenderCategory.SUPPLIER)}>
                  Заявка на поставку материалов
                </a>
              </Button>
            )}
            <Button
              variant="link"
              asChild
              className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
            >
              <a href="#" onClick={onClickRequest(TenderCategory.VEHICLE)}>
                Заявка на Спецтехнику
              </a>
            </Button>
            <Button
              variant="link"
              asChild
              className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
            >
              <a href="#" onClick={onClickRequest(TenderCategory.WORKER)}>
                Заявка на Специалистов
              </a>
            </Button>
          </div>

          <div className="flex flex-col space-y-2">
            <h3 className="text-black font-medium text-base leading-6 tracking-wide m-0">
              Найти
            </h3>
            <Button
              variant="link"
              asChild
              className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
            >
              <Link to="/">Найти СМР</Link>
            </Button>
            <Button
              variant="link"
              asChild
              className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
            >
              <Link to="/suppliers">Найти поставку материалов</Link>
            </Button>
            <Button
              variant="link"
              asChild
              className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
            >
              <Link to="/vehicles">Найти Спецтехнику</Link>
            </Button>
            <Button
              variant="link"
              asChild
              className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
            >
              <Link to="/specialists">Найти Специалистов</Link>
            </Button>
          </div>

          <div className="flex flex-col space-y-2">
            <h3 className="text-black font-medium text-base leading-6 tracking-wide m-0">
              Информация
            </h3>
            <Button
              variant="link"
              asChild
              className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
            >
              <Link to="/about-us">О нас</Link>
            </Button>
            <Button
              variant="link"
              asChild
              className="justify-start p-0 h-auto font-normal text-sm text-muted-foreground hover:text-foreground hover:no-underline"
            >
              <Link to="/privacy">Политика конфиденциальности</Link>
            </Button>
          </div>
        </div>

        <Separator className="my-6" />

        <div className="flex justify-center items-center space-x-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
            asChild
          >
            <a href="#" onClick={onClick}>
              <IconFacebook24 className="h-6 w-6" />
            </a>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
            asChild
          >
            <a
              href="https://www.instagram.com/smrhub.kz/"
              target="_blank"
              rel="noreferrer"
            >
              <IconInstagram24 className="h-6 w-6" />
            </a>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
            asChild
          >
            <a href="#" onClick={onClick}>
              <IconVk24 className="h-6 w-6" />
            </a>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
            asChild
          >
            <a href="https://t.me/Appsmrkz" target="_blank" rel="noreferrer">
              <IconTelegram24 className="h-6 w-6" />
            </a>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
            asChild
          >
            <a href="#" onClick={onClick}>
              <IconYoutube24 className="h-6 w-6" />
            </a>
          </Button>
        </div>

        <p className="text-muted-foreground font-normal text-base leading-6 tracking-wide m-0">
          © 2025, СМР
        </p>
      </Container>

      <ModalTenderCreate
        companyId={!!company ? company.id : null}
        visible={modalTenderCreateVisible}
        closeModal={closeModalTenderCreate}
        tenderCategory={tenderCategory}
        onSuccess={handleTenderCreateSuccess}
      />
    </footer>
  );
}

export default memo(Footer);
